/** @format */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { CourseUnit, CourseUnitSelection, CreditRange, DocumentState, LocalizedString, OtmId } from 'common-typescript/types';
import { Action, InputType } from '../study-action-box/study-action-box.component';

export interface SubstituteCourseUnit {
    id: OtmId;
    name: LocalizedString;
    code: string;
    credits: CreditRange;
    substituteCredits?: number | CreditRange;
    attainedCredits?: number;
    gradeId?: number;
    gradeScaleId?: OtmId;
    isPartialSubstitute?: boolean;
    studyState?: DocumentState;
    isAttainmentAboutToExpire?: boolean;
    attainmentExpiryDate?: string;
    courseUnitSelection?: CourseUnitSelection;
}

@Component({
    selector: 'sis-study-substitution-box',
    templateUrl: './study-substitution-box.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class StudySubstitutionBoxComponent {
    @Input() courseUnitGroupId: OtmId;
    /**
     * Determines course units' headers' semantic aria-level for screen readers, default is equivalent for h4
     */
    @Input() level? = 4;
    @Input() substituteCourseUnits: SubstituteCourseUnit[] = [];
    @Input() courseUnitsById: { [id: string]: CourseUnit } = {};
    @Input() showCompletionMethodSelection: boolean = false;
    @Input() showAttainment: boolean = true;
    // The student plan uses texts addressed to the student, other views (e.g. staff) use the neutral texts
    @Input() substitutionTextVariant: 'neutral' | 'student' = 'neutral';
    // The action applies to the substituted course unit, not to the substitutes
    @Input() action?: Action;
    @Input() selected?: boolean;
    @Input() inputType?: InputType;
    @Input() actionInputId: string;
    @Input() actionInputName: string;
    @Input() studyInfo: string;
    @Output() nameClickAction = new EventEmitter<OtmId>();
    @Output() editSubstitutionAction = new EventEmitter<void>();
    @Output() actionChange = new EventEmitter<void>();

    getSubstituteCredits(substituteCourseUnits: SubstituteCourseUnit[]): CreditRange {
        const credits: CreditRange = { min: 0, max: 0 };
        substituteCourseUnits.forEach(s => {
            if (typeof s?.substituteCredits === 'number') {
                credits.min += s.substituteCredits;
                credits.max += s.substituteCredits;
            } else {
                if (s.substituteCredits && 'min' in s.substituteCredits) {
                    credits.min += s.substituteCredits.min ?? 0;
                }
                if (s.substituteCredits && 'max' in s.substituteCredits) {
                    credits.max += s.substituteCredits.max ?? 0;
                }
            }
        });
        return credits;
    }

    isPartialSubstitute(substituteCourseUnits: SubstituteCourseUnit[]): boolean {
        return !!substituteCourseUnits.find(s => !!s.isPartialSubstitute);
    }

    getSelectedSubstitutionKey(plural: boolean): string {
        if (this.substitutionTextVariant === 'student') {
            return plural ? 'SIS_COMPONENTS.STUDY.STUDENT_SELECTED_SUBSTITUTIONS' : 'SIS_COMPONENTS.STUDY.STUDENT_SELECTED_SUBSTITUTION';
        }
        return plural ? 'SIS_COMPONENTS.STUDY.SELECTED_SUBSTITUTIONS' : 'SIS_COMPONENTS.STUDY.SELECTED_SUBSTITUTION';
    }

    getCourseUnit(courseUnitId: OtmId): CourseUnit {
        return this.courseUnitsById[courseUnitId];
    }
}
